import {useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, Card, CardActionArea, CardContent, Chip, Typography} from "@mui/material";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";
import {Navigate, useNavigate} from "react-router-dom";
import KioskHeader from "../components/KioskHeader";
import KioskSteps from "../components/KioskSteps";
import ProductCustomizer from "../components/ProductCustomizer";
import {useKioskLang, useKioskSession} from "../context/KioskSessionContext";
import {cartTotal, getVisibleCategories, getVisibleMeals} from "../orderUtils";

//滾動判定線：距容器頂部多少 px 內的分類標題視為「目前分類」（略大於 sticky 標題高度）
const SPY_OFFSET = 90;

export default function MenuPage({systemData, online, onHome}) {
  const navigate = useNavigate();
  const {state, dispatch} = useKioskSession();
  const {tr, name} = useKioskLang();
  const categories = useMemo(() => getVisibleCategories(systemData), [systemData]);
  const sections = useMemo(
    () => categories
      .map((category) => ({category, meals: getVisibleMeals(systemData, category.category_id)}))
      .filter((section) => section.meals.length > 0),
    [systemData, categories]
  );
  const [activeId, setActiveId] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const scrollRef = useRef(null);
  const sectionRefs = useRef({});
  const navRefs = useRef({});
  //點分類後平滑捲動期間，先鎖定目標分類、暫停 scroll-spy，避免捲動過程反白亂跳
  const jumpTargetRef = useRef(null);

  useEffect(() => {
    const valid = sections.some(({category}) => String(category.category_id) === String(activeId));
    if (!valid && sections[0]) setActiveId(sections[0].category.category_id);
  }, [sections, activeId]);

  //反白的分類自動保持在左側清單可視範圍內
  useEffect(() => {
    if (activeId != null) navRefs.current[activeId]?.scrollIntoView({block: "nearest"});
  }, [activeId]);

  if (!state.sessionId || !state.orderMethod) {
    return <Navigate to="/kiosk/welcome" replace/>;
  }
  const cartCount = state.cart.reduce((sum, item) => sum + Number(item.qty), 0);
  const total = cartTotal(systemData, state.cart);

  const saveItem = (item) => {
    dispatch({type: "ADD_ITEM", item});
    setSelectedMeal(null);
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    if (jumpTargetRef.current) {
      //還在往目標分類平滑捲動：到達、已捲到底、或逾時（被手動捲動打斷）才恢復 scroll-spy
      const {top, until} = jumpTargetRef.current;
      const arrived = Math.abs(container.scrollTop - top) < 6
        || container.scrollTop + container.clientHeight >= container.scrollHeight - 6
        || Date.now() > until;
      if (!arrived) return;
      jumpTargetRef.current = null;
    }
    const anchor = container.scrollTop + SPY_OFFSET;
    let current = sections[0]?.category.category_id;
    sections.forEach(({category}) => {
      const node = sectionRefs.current[category.category_id];
      if (node && node.offsetTop <= anchor) current = category.category_id;
    });
    if (current != null && String(current) !== String(activeId)) setActiveId(current);
  };

  const jumpTo = (categoryId) => {
    const container = scrollRef.current;
    const node = sectionRefs.current[categoryId];
    if (!container || !node) return;
    setActiveId(categoryId);
    const top = Math.max(0, Math.min(node.offsetTop, container.scrollHeight - container.clientHeight));
    jumpTargetRef.current = {top, until: Date.now() + 1500};
    container.scrollTo({top, behavior: "smooth"});
  };

  return <Box sx={{height: "100dvh", display: "flex", flexDirection: "column", bgcolor: "#F7F1E8"}}>
    <KioskHeader systemData={systemData} onHome={onHome} method={state.orderMethod} tableNumber={state.tableNumber} cartCount={cartCount} onBack={() => navigate("/kiosk/service")} onCart={() => navigate("/kiosk/cart")} online={online}/>
    <KioskSteps active={0}/>
    <Box sx={{flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "220px 1fr"}}>
      <Box component="nav" aria-label="商品分類" sx={{p: 2, overflow: "auto", bgcolor: "#EEE2D5", borderRight: "1px solid rgba(80,55,40,.1)"}}>
        <Typography sx={{fontSize: 17, fontWeight: 900, px: 1.5, py: 1, color: "text.secondary"}}>{tr("categories")}</Typography>
        {sections.map(({category}) => {
          const active = String(activeId) === String(category.category_id);
          return <Button key={category.category_id} ref={(node) => {navRefs.current[category.category_id] = node;}} fullWidth variant={active ? "contained" : "text"} onClick={() => jumpTo(category.category_id)} sx={{minHeight: 64, justifyContent: "flex-start", px: 2, my: .7, color: active ? "#FFF" : "text.primary"}}>{name(category)}</Button>;
        })}
      </Box>
      <Box ref={scrollRef} onScroll={handleScroll} sx={{position: "relative", overflow: "auto", px: 3, pb: 3, scrollBehavior: "smooth"}}>
        {sections.map(({category, meals}) => (
          <Box key={category.category_id} ref={(node) => {sectionRefs.current[category.category_id] = node;}}>
            <Typography sx={{position: "sticky", top: 0, zIndex: 2, fontSize: 30, fontWeight: 950, py: 2, mb: .5, bgcolor: "#F7F1E8"}}>{name(category)}</Typography>
            <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 2.5, mb: 3.5}}>
              {meals.map((meal) => {
                const unavailable = Number(meal.status) >= 2;
                const imageUrl = meal.image_url || meal.image;
                return <Card key={meal.id} sx={{position: "relative", border: "1px solid rgba(80,55,40,.1)", boxShadow: "0 12px 30px rgba(76,49,32,.08)", opacity: unavailable ? .66 : 1}}>
                  <CardActionArea disabled={unavailable} onClick={() => setSelectedMeal(meal)} sx={{height: "100%"}}>
                    <Box sx={{aspectRatio: "4 / 3", bgcolor: "#E9DACB", display: "grid", placeItems: "center", overflow: "hidden"}}>
                      {imageUrl ? <Box component="img" src={imageUrl} alt={name(meal)} loading="lazy" onError={(event) => {event.currentTarget.style.display = "none";}} sx={{width: "100%", height: "100%", objectFit: "cover"}}/> : <ImageNotSupportedRoundedIcon sx={{fontSize: 54, color: "#B39D8D"}}/>}
                    </Box>
                    <CardContent sx={{p: 2}}>
                      <Box sx={{display: "flex", gap: 1, alignItems: "flex-start"}}>
                        <Typography sx={{fontSize: 21, lineHeight: 1.3, fontWeight: 950, flex: 1}}>{name(meal)}</Typography>
                        {meal.badge || meal.tag ? <Chip size="small" color="primary" label={meal.badge || meal.tag}/> : null}
                      </Box>
                      <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2}}>
                        <Typography sx={{fontSize: 23, fontWeight: 950, color: "primary.main"}}>${Number(meal.price ?? 0).toLocaleString()}</Typography>
                        <AddShoppingCartRoundedIcon color="primary"/>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                  {unavailable && <Box sx={{position: "absolute", inset: 0, display: "grid", placeItems: "center", bgcolor: "rgba(40,31,25,.5)", color: "#FFF", fontSize: 28, fontWeight: 950, pointerEvents: "none"}}>{tr("soldOutToday")}</Box>}
                </Card>;
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
    {cartCount > 0 && (
      <Box sx={{flex: "0 0 auto", px: 3, py: 1.75, pb: "calc(14px + env(safe-area-inset-bottom, 0px))", bgcolor: "#FFF", borderTop: "1px solid rgba(80,55,40,.12)", boxShadow: "0 -10px 30px rgba(76,49,32,.1)", display: "flex", alignItems: "center", gap: 2, animation: "kioskSlideUp .28s ease"}}>
        <Box key={cartCount} sx={{animation: "kioskPop .3s ease"}}>
          <Typography sx={{fontSize: 16, color: "text.secondary", fontWeight: 800}}>{tr("pickedItems", {n: cartCount})}</Typography>
          <Typography sx={{fontSize: 27, fontWeight: 950, color: "primary.main", lineHeight: 1.15}}>${total.toLocaleString()}</Typography>
        </Box>
        <Button variant="contained" onClick={() => navigate("/kiosk/cart")} endIcon={<ArrowForwardRoundedIcon sx={{animation: "kioskArrow 1.2s infinite"}}/>} sx={{ml: "auto", minWidth: 330, minHeight: 68, fontSize: 22, animation: "kioskPulse 2s infinite"}}>
          {tr("nextToCart")}
        </Button>
      </Box>
    )}
    <ProductCustomizer open={Boolean(selectedMeal)} meal={selectedMeal} systemData={systemData} onClose={() => setSelectedMeal(null)} onSave={saveItem}/>
  </Box>;
}
